import type { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import type { ReactNode } from 'react';

import getSelectedAccount from 'watchtower-ui/api/msalAccount';

export const USER_ROLES = {
  AGENCY: 'Agency',
} as const;

export interface RoleVisibility {
  allowedRoles?: readonly string[];
  excludedRoles?: readonly string[];
}

export const ROLE_FEATURES = {
  POD_OPTIMIZER: { excludedRoles: [USER_ROLES.AGENCY] },
  PRICE_ELASTICITY: { excludedRoles: [USER_ROLES.AGENCY] },
} as const satisfies Record<string, RoleVisibility>;

const ABWP_ROLE_CLAIM = 'abwpRole';
const AWBP_ROLE_CLAIM = 'awbpRole';

const normalizeRole = (role: string): string => role.trim().toLowerCase();

const hasRole = (role: string, roles: readonly string[]): boolean =>
  roles.some((candidate) => normalizeRole(candidate) === normalizeRole(role));

export const getAccountRole = (account: AccountInfo | null): string | undefined => {
  const claims = account?.idTokenClaims;
  const role = claims?.[ABWP_ROLE_CLAIM] ?? claims?.[AWBP_ROLE_CLAIM];

  return typeof role === 'string' ? role : undefined;
};

export const isRoleVisible = (
  role: string | undefined,
  { allowedRoles, excludedRoles }: Readonly<RoleVisibility>,
): boolean => {
  if (!role) {
    return allowedRoles === undefined;
  }

  if (allowedRoles && !hasRole(role, allowedRoles)) {
    return false;
  }

  return !excludedRoles || !hasRole(role, excludedRoles);
};

export const useRoleVisibility = (roleVisibility: Readonly<RoleVisibility>): boolean => {
  const { accounts, instance } = useMsal();
  const account = getSelectedAccount({ accounts, instance });

  return isRoleVisible(getAccountRole(account), roleVisibility);
};

export interface RoleProtectedProps extends RoleVisibility {
  children: ReactNode;
  fallback?: ReactNode;
}

export const RoleProtected = ({
  allowedRoles,
  children,
  excludedRoles,
  fallback = null,
}: Readonly<RoleProtectedProps>) => {
  const isVisible = useRoleVisibility({ allowedRoles, excludedRoles });

  return isVisible ? children : fallback;
};
